// LeadForm.tsx — the one contact form the widget renders in two roles:
//
//  - "gate":   the pre-chat form every visitor fills before a chat opens
//              (name / phone / what they're inquiring about).
//  - "outage": graceful degradation when the chat brain is unreachable —
//              same fields, "leave a message" framing. Replaces the old
//              standalone FallbackForm component.
//
// The parent owns the network call via onSubmit (gate → /chat/session with a
// prechat block; outage → /chat/fallback-message), so this component is pure
// fields + lenient validation + a submitting guard. Validation is deliberately
// forgiving: a lead is never lost over formatting (see validate.ts).

import { JSX } from "preact";
import { useState, useCallback } from "preact/hooks";
import { isPlausiblePhone } from "../validate";

export interface LeadFormValues {
  first_name: string;
  last_name: string | null;
  phone: string;
  inquiry: string;
}

interface Props {
  /** Headline above the fields, rendered with a live-status dot — says WHY
   *  the visitor is filling this in ("Connect with a live assistant"). */
  title?: string;
  /** Copy above the fields — role-specific, parent-supplied. */
  intro: string;
  submitLabel: string;
  /** Button text while the submit is in flight ("Connecting…" for the gate,
   *  "Sending…" for the outage capture). */
  submittingLabel?: string;
  /** Prefill (returning visitor's saved contact, a starter-chip inquiry, or
   *  the values preserved across a failed submit). */
  initialValues?: Partial<LeadFormValues>;
  /** External error from the parent's network path (e.g. degrade ladder
   *  exhausted) — rendered above the submit button. */
  error?: string | null;
  /** Parent-owned submit. The form disables itself until the promise settles. */
  onSubmit: (values: LeadFormValues) => Promise<void>;
}

export function LeadForm({ title, intro, submitLabel, submittingLabel, initialValues, error, onSubmit }: Props): JSX.Element {
  const [firstName, setFirstName] = useState(initialValues?.first_name ?? "");
  const [lastName, setLastName] = useState(initialValues?.last_name ?? "");
  const [phone, setPhone] = useState(initialValues?.phone ?? "");
  const [inquiry, setInquiry] = useState(initialValues?.inquiry ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const submit = useCallback(async () => {
    if (submitting) return;

    if (!firstName.trim() || !phone.trim() || !inquiry.trim()) {
      setFieldError("Please fill in your first name, phone, and what you're inquiring about.");
      return;
    }
    if (!isPlausiblePhone(phone)) {
      setFieldError("That phone number doesn't look complete — mind double-checking it?");
      return;
    }

    setFieldError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        // Server caps: first/last 100, phone 30, inquiry 2000 — clamp instead
        // of rejecting so an over-long paste never blocks the submission.
        first_name: firstName.trim().slice(0, 100),
        last_name: lastName.trim().slice(0, 100) || null,
        phone: phone.trim().slice(0, 30),
        inquiry: inquiry.trim().slice(0, 2000),
      });
    } finally {
      setSubmitting(false);
    }
  }, [firstName, lastName, phone, inquiry, submitting, onSubmit]);

  return (
    <div class="acc-fallback">
      {title && (
        <h3 class="acc-lead-title">
          <span class="acc-live-dot" aria-hidden="true" />
          {title}
        </h3>
      )}
      <p class="acc-fallback-intro">{intro}</p>
      <input
        class="acc-fallback-input"
        type="text"
        placeholder="First name *"
        value={firstName}
        disabled={submitting}
        maxLength={100}
        onInput={(e) => setFirstName((e.currentTarget as HTMLInputElement).value)}
        aria-label="First name"
        required
      />
      <input
        class="acc-fallback-input"
        type="text"
        placeholder="Last name"
        value={lastName}
        disabled={submitting}
        maxLength={100}
        onInput={(e) => setLastName((e.currentTarget as HTMLInputElement).value)}
        aria-label="Last name"
      />
      <input
        class="acc-fallback-input"
        type="tel"
        placeholder="Phone number *"
        value={phone}
        disabled={submitting}
        maxLength={30}
        onInput={(e) => setPhone((e.currentTarget as HTMLInputElement).value)}
        aria-label="Phone number"
        required
      />
      <textarea
        class="acc-fallback-textarea"
        rows={4}
        placeholder="What are you inquiring about? *"
        value={inquiry}
        disabled={submitting}
        maxLength={2000}
        onInput={(e) => setInquiry((e.currentTarget as HTMLTextAreaElement).value)}
        aria-label="What are you inquiring about"
        required
      />
      {(fieldError || error) && (
        <div class="acc-error" role="alert">{fieldError || error}</div>
      )}
      <button
        type="button"
        class="acc-fallback-submit"
        disabled={submitting}
        onClick={() => void submit()}
      >
        <span>{submitting ? (submittingLabel ?? "Sending…") : submitLabel}</span>
        {!submitting && (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M5 12h14M13 5l7 7-7 7"></path>
          </svg>
        )}
      </button>
      <p class="acc-fallback-disclaimer">
        Not legal advice. If this is an emergency, call 911.
      </p>
    </div>
  );
}
